/**
 * Tokens exclusivos del panel administrativo (chrome: sidebar, topbar,
 * cards de las pantallas /admin/*). Es un grupo aparte de `colors.brand`
 * y de los `semanticTokens` del sitio público a propósito: el público
 * sigue usando `accent.default`, `surface.*`, `text.*`, `brand.*` sin
 * cambios, y estos tokens no los tocan ni los sustituyen.
 *
 * Valores exactos fijados por la maqueta de referencia del panel.
 */
const adminPanel = {
  navy: '#06295a',
  navyDark: '#031C40',
  navyHover: '#123f78',
  blue: '#2563eb',
  yellow: '#f5a623',
  yellowHover: '#DE8F0D',
  bg: '#f5f7fa',
  cardBorder: '#e4e9f0',
  text: '#172033',
  textMuted: '#667085',
  success: '#16A34A',
  warning: '#D97706',
  danger: '#DC2626',
  // Borde inferior de la topbar. Antes se llamaba `searchBorder` -nació
  // con el buscador global de la topbar-; el buscador se eliminó (ver
  // AdminTopbar), pero el valor sigue vivo como borde de la topbar, así
  // que se renombró en vez de dejar un nombre que ya no describe nada.
  topbarBorder: '#e2e8f0',
  // Separador entre filas de las tablas de Productos/Proyectos. Valor
  // distinto de `cardBorder` a propósito, pedido explícitamente en la
  // especificación de la escala compacta.
  rowBorder: '#e5eaf0',
};

export default adminPanel;

/**
 * Medidas del chrome del panel (sidebar, topbar, contenido) y de sus
 * controles. Se exportan como constantes JS planas -no como parte del
 * objeto de arriba- porque `theme.colors.adminPanel` está fijado por un
 * `toEqual` exacto en `theme.test.js`; mezclar aquí anchos/alturas
 * rompería esa prueba sin aportar nada (Chakra resuelve `w`/`h` contra
 * `theme.sizes`, no contra `theme.colors`).
 *
 * Toda pantalla o componente del panel que necesite estas medidas las
 * importa desde aquí: es la única fuente, así que compactar la escala
 * es cambiar un número en un solo sitio, nunca un valor suelto por
 * pantalla.
 */
export const adminLayout = {
  sidebarWidth: '208px',
  topbarHeight: '58px',
  contentMaxWidth: '1600px',
  contentPadding: '20px',
  contentPaddingMobile: '12px',
  controlHeight: '38px',
  buttonHeight: '38px',
  // Coincide a propósito con `theme.radii.adminCard` (ver radii.js): un
  // solo valor de origen para el radio de card de todo el panel.
  cardRadius: '10px',
};

/**
 * Tipografía centralizada del panel. Mismo criterio que `adminLayout`:
 * constantes planas, importadas donde haga falta, para que ninguna
 * pantalla escriba un `fontSize` suelto por su cuenta.
 */
export const adminTypography = {
  bodyFontSize: '14px',
  smallFontSize: '12px',
  tableHeaderSize: '11px',
};

/**
 * Amplía el área táctil de un control cuyo tamaño visual es menor que
 * `targetSize` (44px por defecto, el mínimo recomendado para objetivos
 * táctiles) sin cambiar su tamaño renderizado: añade un `::before`
 * absoluto con `inset` negativo, que sigue contando como parte del
 * elemento a efectos de hit-testing aunque se pinte fuera de su caja.
 *
 * Se usa en botones de `controlHeight`/`buttonHeight` (38px) y en los
 * botones de acción icon-only de las tablas (32px): la densidad visual
 * de la escala compacta no debe sacrificar accesibilidad táctil.
 */
export function expandHitArea(visualSize, targetSize = 44) {
  const pad = Math.max(0, (targetSize - visualSize) / 2);
  if (pad === 0) return {};

  return {
    position: 'relative',
    _before: {
      content: '""',
      position: 'absolute',
      inset: `-${pad}px`,
    },
  };
}
